/*
 * Slot keys and their display / progression order (owner rule 16 Section I, Half / Quarter era; 2026-10-11).
 * The contract keys slots with a uint8: 1..16 = Slot 1..16, 50 = Slot ½ (Half era), 25 = Slot ¼ (Quarter era).
 * Progression is ¼ -> ½ -> 1 -> 2 ... 16, so never compare or sort slot numbers numerically - 50 is BELOW Slot 1.
 * Mirrors apps/api/src/matrix/slotOrder.ts.
 */

export const SLOT_HALF = 50;
export const SLOT_QUARTER = 25;
export const LAST_SLOT = 16;

/** Sub-slots below Slot 1, lowest first. */
export const SUB_SLOTS: readonly number[] = [SLOT_QUARTER, SLOT_HALF];

/* Slot / sub-slot seat values are never hard-coded here: they are the contract's slotValue(n), sent by the API. */

export function isSubSlot(slot: number): boolean {
  return slot === SLOT_HALF || slot === SLOT_QUARTER;
}

/** Progression rank: ¼ = -1, ½ = 0, Slot N = N. */
export function slotRank(slot: number): number {
  if (slot === SLOT_QUARTER) return -1;
  if (slot === SLOT_HALF) return 0;
  return slot;
}

/** Sort comparator in progression order (¼, ½, 1 .. 16). */
export function compareSlots(a: number, b: number): number {
  return slotRank(a) - slotRank(b);
}

/** A copy of `slots`, unique and in progression order. */
export function sortSlots(slots: readonly number[]): number[] {
  return [...new Set(slots)].sort(compareSlots);
}

/** '¼' | '½' | '1'..'16'. */
export function slotLabel(slot: number): string {
  if (slot === SLOT_HALF) return '½';
  if (slot === SLOT_QUARTER) return '¼';
  return String(slot);
}

/** "Slot ½", "Slot 3", with " · virtual" for a virtual sub-slot. */
export function slotTitle(slot: number, virtual = false): string {
  return `Slot ${slotLabel(slot)}${virtual ? ' · virtual' : ''}`;
}

/** The slot an upgrade from `slot` opens (¼ -> ½ -> 1 -> 2 ... 16); null after Slot 16. */
export function nextSlot(slot: number): number | null {
  if (slot === SLOT_QUARTER) return SLOT_HALF;
  if (slot === SLOT_HALF) return 1;
  return slot >= LAST_SLOT ? null : slot + 1;
}

/** The slot below `slot` in progression order; null for Slot ¼. */
export function previousSlot(slot: number): number | null {
  if (slot === SLOT_QUARTER) return null;
  if (slot === SLOT_HALF) return SLOT_QUARTER;
  if (slot === 1) return SLOT_HALF;
  return slot - 1;
}

/** Highest slot by rank; 0 when the list is empty. */
export function highestSlot(slots: readonly number[]): number {
  let best = 0;
  for (const slot of slots) if (best === 0 || compareSlots(slot, best) > 0) best = slot;
  return best;
}

/** A slot key the app can show: 1..16, 50 or 25. */
export function isSlotKey(slot: number): boolean {
  return isSubSlot(slot) || (Number.isInteger(slot) && slot >= 1 && slot <= LAST_SLOT);
}
